<template>
  <div v-if="customAccount">
    <div class="d-flex justify-content-between mt-5 mb-4">
      <div class="page-subtitle">Payout Details</div>
      <div>
        <b-button
          class="btn-outlined bg-white text-dark edit-btn"
          variant="outline-secondary"
          v-show="externalAccounts.length > 0"
          @click="isEditing = !isEditing"
          >{{ isEditing ? 'Cancel' : 'Edit' }}</b-button
        >
      </div>
    </div>

    <div v-if="!loading && externalAccounts.length === 0">
      {{ displayText }}
    </div>

    <b-modal
      v-model="confirmDelete"
      title="Delete Bank Account?"
      centered
      class="p-3"
    >
      <p>
        Are you sure you want to permanently delete this bank account?
        <br />All details will be deleted.
      </p>
      <hr class="modal-divider" />
      <template v-slot:modal-footer>
        <b-button class="btn btn-secondary" @click="confirmDelete = false"
          >Cancel</b-button
        >
        <b-button variant="primary" :disabled="loading" @click="onClickDelete">
          <b-spinner small v-if="loading"></b-spinner>&nbsp;Delete
        </b-button>
      </template>
    </b-modal>

    <div v-if="loading">
      <card-shimmer
        v-for="i in 3"
        :key="i"
        width="100%"
        height="4.5em"
        :rounded="true"
        class="mb-4"
      ></card-shimmer>
    </div>

    <b-list-group v-else>
      <b-list-group-item
        class="c-pointer mb-4"
        v-for="account in externalAccounts"
        :key="account.id"
        :class="
          account.default_for_currency && !isEditing ? 'default-item' : ''
        "
        @click="!isEditing ? setDefaultCard(account.id) : ''"
      >
        <div class="d-flex">
          <b-img class="mt-1" v-bind="mainProps" rounded alt="Logo"></b-img>
          <div class="ml-4">
            {{ account.bank_name }}
            <div>
              {{ account.routing_number }}
              <span class="ml-4">•••••&nbsp;&nbsp;{{ account.last4 }}</span>
              <div
                class="remove-link"
                v-show="account.default_for_currency && !isEditing"
              >
                <b-link>
                  <b-icon scale="1.8" icon="check"></b-icon>
                </b-link>
              </div>
              <div
                class="remove-link"
                @click="onClickRemove(account.id)"
                v-show="isEditing"
              >
                <b-link>Remove</b-link>
              </div>
            </div>
          </div>
        </div>
      </b-list-group-item>
    </b-list-group>

    <b-modal
      v-model="show"
      title="Add Bank Account"
      centered
      hide-footer
      size="lg"
      content-class="px-4"
    >
      <payout-form
        :full-name="user.full_name"
        :account-id="customAccount.id"
        @onSuccess="show = false"
      />
    </b-modal>

    <b-row class="mt-5">
      <b-col md="6" sm="6">
        <b-button
          block
          type="submit"
          variant="primary"
          :disabled="loading"
          @click="showAddButton ? addBankAccount() : getStripeLink()"
        >
          <b-spinner small v-if="loading"></b-spinner>
          <!-- eslint-disable-next-line -->
          {{ buttonText }}
        </b-button>
      </b-col>
    </b-row>

    <b-alert
      class="custom-alert"
      :show="dismissCountDown"
      dismissible
      fade
      variant="primary"
      @dismiss-count-down="countDownChanged"
      >Your default bank account has been changed</b-alert
    >
  </div>
  <div v-else>
    <div class="d-flex justify-content-between mt-5 mb-4">
      <div class="page-subtitle">Payout Details</div>
    </div>
    Error: {{ errorMessage }}
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import PayoutForm from '@/components/forms/PayoutForm'
import CardShimmer from '@/components/shimmers/shimmer'

export default {
  components: {
    PayoutForm,
    CardShimmer
  },

  computed: {
    ...mapState({
      user: state => state.auth.user,
      customAccount: state => state.account.customAccount,
      externalAccounts: state => state.account.list
    }),

    buttonText() {
      if (this.customAccount.requirements.currently_due.length > 3) {
        return ' Connect with Stripe'
      } else if (!this.showAddButton) {
        return ' Upload Documents'
      } else {
        return ' Add Bank Account'
      }
    },

    displayText() {
      if (this.customAccount.requirements.currently_due.length > 3) {
        return 'Connect with Stripe to add your basic information'
      } else if (!this.showAddButton) {
        return 'Connect with Stripe to upload identity verification requirements'
      } else {
        return 'Onboarding requirements have been submitted and verified'
      }
    },

    showAddButton() {
      return (
        this.customAccount.payouts_enabled ||
        (this.customAccount.requirements.pending_verification.length === 0 &&
          this.customAccount.requirements.eventually_due.length === 1 &&
          this.customAccount.requirements.eventually_due[0] ===
            'external_account')
      )
    }
  },

  data() {
    return {
      loading: false,
      show: false,
      isEditing: false,
      confirmDelete: false,
      current: 0,
      dismissCountDown: 0,
      errorMessage: null,
      mainProps: {
        blank: true,
        blankColor: '#2C66D5',
        width: 40,
        height: 40
      }
    }
  },

  methods: {
    ...mapActions({
      getCustomAccount: 'account/getCustomAccount',
      createCustomAccount: 'account/createCustomAccount',
      deleteExternalAccount: 'account/deleteExternalAccount',
      setExternalAccountAsPrimary: 'account/setExternalAccountAsPrimary'
    }),

    countDownChanged(dismissCountDown) {
      this.dismissCountDown = dismissCountDown
    },

    addBankAccount() {
      this.show = true
    },

    onClickRemove(id) {
      this.current = id
      this.confirmDelete = true
    },

    async onClickDelete() {
      this.loading = true
      await this.deleteExternalAccount(this.current)
      this.loading = false
      this.confirmDelete = false
    },

    async setDefaultCard(id) {
      this.loading = true
      await this.setExternalAccountAsPrimary(id)
      this.loading = false
      this.dismissCountDown = 3
    },

    async fetchAccount() {
      this.loading = true
      await this.getCustomAccount().catch(error => {
        const errorCode = error.response.data.error_code
        if (errorCode === 'STRIPE_CONNECT_NOT_FOUND') {
          this.setupStripeConnect()
        }
      })
      this.loading = false
    },

    async getStripeLink() {
      this.loading = true
      const formData = {
        return_url: `https://baseplate-web.appetiserdev.tech/settings#payouts`,
        type: 'account_onboarding',
        collect: 'eventually_due'
      }
      await this.$api
        .post(`auth/connect/account-links`, formData)
        .then(({ data }) => {
          window.open(data.data.url, '_blank')
        })
      this.loading = false
    },

    async setupStripeConnect() {
      this.loading = true
      await this.createCustomAccount({
        first_name: this.user.first_name,
        last_name: this.user.last_name
      }).catch(error => {
        const data = error.response.data
        if (data.error_code === 'HTTP_BAD_REQUEST') {
          this.errorMessage = data.message
        }
      })
      this.loading = false
    }
  },

  created() {
    this.fetchAccount()
  }
}
</script>

<style lang="scss" scoped>
.list-group-item {
  border-radius: 8px;
  border: 2px solid #e5e5e5;
}
.remove-link {
  position: absolute;
  right: 20px;
  margin-top: -35px;
}
.default-item {
  border: 2px solid #2c66d5;
  color: #2c66d5;
}
.edit-btn {
  width: 150px;
  height: 50px;
  padding: 10px;
}
</style>
